import React from 'react';
import { motion } from 'framer-motion';
import { LayoutDashboardIcon, ListIcon, HomeIcon, UsersIcon, EyeIcon, FileTextIcon, TrendingUpIcon, ArrowRightIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, PhoneIcon, CalendarIcon, PlusIcon, UploadIcon, UserPlusIcon, CalendarPlusIcon, FileSignatureIcon, BellIcon, ChevronRightIcon, HandshakeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRental } from '../contexts/RentalContext';
export function Rental() {
  const {
    prospectingLeads,
    listings,
    tenantLeads,
    viewings,
    contracts,
    getStats
  } = useRental();
  const stats = getStats();
  // Calculate additional stats
  const pendingCalls = prospectingLeads.filter(l => !l.callCompleted).length;
  const newLeadsToday = prospectingLeads.filter(l => {
    const today = new Date().toISOString().split('T')[0];
    return l.listDate === today;
  }).length;
  const collaborations = prospectingLeads.filter(l => l.collaboration === true).length;
  const upcomingViewings = viewings.filter(v => {
    const viewingDate = new Date(v.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return viewingDate >= today && viewingDate < tomorrow && v.status === 'scheduled';
  });
  const needsFeedback = viewings.filter(v => {
    const viewingDateTime = new Date(`${v.date}T${v.time}`);
    const now = new Date();
    return viewingDateTime < now && (v.status === 'scheduled' || v.status === 'rescheduled');
  });
  const activeContracts = contracts.filter(c => c.status === 'draft' || c.status === 'sent');
  const signedContracts = contracts.filter(c => c.status === 'signed');
  // Main stats cards
  const dashboardStats = [{
    label: 'Liste Prospectare',
    value: stats.totalProspectingLeads,
    icon: ListIcon,
    gradient: 'from-blue-500 to-indigo-600',
    bgGradient: 'from-blue-50 to-indigo-50',
    iconBg: 'from-blue-100 to-indigo-100',
    path: '/inchiriere/liste-prospectare',
    subtitle: `${pendingCalls} de apelat`,
    change: newLeadsToday > 0 ? `+${newLeadsToday} astăzi` : null
  }, {
    label: 'Colaborări',
    value: collaborations,
    icon: HandshakeIcon,
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
    iconBg: 'from-emerald-100 to-teal-100',
    path: '/inchiriere/liste-prospectare',
    subtitle: 'confirmate',
    change: null
  }, {
    label: 'Leaduri Chiriași',
    value: stats.activeTenantLeads,
    icon: UsersIcon,
    gradient: 'from-orange-500 to-red-600',
    bgGradient: 'from-orange-50 to-red-50',
    iconBg: 'from-orange-100 to-red-100',
    path: '/inchiriere/leaduri',
    subtitle: `${tenantLeads.filter(l => l.status === 'qualified').length} calificați`,
    change: null
  }, {
    label: 'Vizionări',
    value: upcomingViewings.length,
    icon: EyeIcon,
    gradient: 'from-purple-500 to-pink-600',
    bgGradient: 'from-purple-50 to-pink-50',
    iconBg: 'from-purple-100 to-pink-100',
    path: '/inchiriere/vizionari',
    subtitle: needsFeedback.length > 0 ? `${needsFeedback.length} necesită feedback` : 'programate azi/mâine',
    change: null
  }];
  // Recent activity from all sections
  const recentActivity = [...prospectingLeads.slice(0, 2).map(lead => ({
    id: `lead-${lead.id}`,
    type: 'prospecting',
    title: lead.collaboration ? 'Colaborare nouă confirmată' : 'Lead nou de prospectare',
    description: `${lead.zone} • ${lead.rooms} camere • ${lead.price}€`,
    time: 'Astăzi',
    icon: lead.collaboration ? HandshakeIcon : ListIcon,
    color: lead.collaboration ? 'text-emerald-600' : 'text-blue-600',
    bg: lead.collaboration ? 'bg-emerald-100' : 'bg-blue-100',
    path: '/inchiriere/liste-prospectare'
  })), ...tenantLeads.slice(0, 2).map(lead => ({
    id: `tenant-${lead.id}`,
    type: 'tenant',
    title: `Lead chiriaș: ${lead.name}`,
    description: `Buget ${lead.budget}€ • ${lead.rooms} camere • ${lead.preferredZones.join(', ')}`,
    time: new Date(lead.createdAt).toLocaleDateString('ro-RO'),
    icon: UsersIcon,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    path: '/inchiriere/leaduri'
  })), ...upcomingViewings.slice(0, 1).map(viewing => ({
    id: `viewing-${viewing.id}`,
    type: 'viewing',
    title: `Vizionare programată: ${viewing.clientName}`,
    description: `${viewing.date} la ${viewing.time}`,
    time: 'Programată',
    icon: EyeIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    path: '/inchiriere/vizionari'
  }))].slice(0, 5);
  // Tasks and alerts
  const urgentTasks = [...needsFeedback.map(v => ({
    id: `feedback-${v.id}`,
    task: `Adaugă feedback pentru ${v.clientName}`,
    description: `Vizionare din ${v.date} la ${v.time}`,
    priority: 'high' as const,
    status: 'pending' as const,
    action: () => window.location.href = '/inchiriere/vizionari'
  })), ...prospectingLeads.filter(l => !l.callCompleted).slice(0, 3).map(l => ({
    id: `call-${l.id}`,
    task: `Apelează proprietar ${l.zone}`,
    description: `${l.rooms} camere • ${l.price}€`,
    priority: 'medium' as const,
    status: 'pending' as const,
    action: () => window.location.href = '/inchiriere/liste-prospectare'
  })), ...upcomingViewings.slice(0, 2).map(v => ({
    id: `confirm-${v.id}`,
    task: `Confirmă vizionare ${v.clientName}`,
    description: `${v.date} la ${v.time}`,
    priority: 'medium' as const,
    status: 'pending' as const,
    action: () => window.location.href = '/inchiriere/vizionari'
  })), ...activeContracts.slice(0, 2).map(c => ({
    id: `contract-${c.id}`,
    task: `Finalizează contract ${c.clientName}`,
    description: c.status === 'draft' ? 'Draft' : 'Trimis spre semnare',
    priority: 'high' as const,
    status: 'pending' as const,
    action: () => window.location.href = '/inchiriere/contracte'
  }))].slice(0, 6);
  return <div className="min-h-screen" data-id="element-9041">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-20 pt-10" data-id="element-9042">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-9043" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-9044"></div>

        {/* Decorative blur elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 transform translate-x-1/2 -translate-y-1/2" data-id="element-9045"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500 rounded-full blur-3xl opacity-20 transform -translate-x-1/2 translate-y-1/2" data-id="element-9046"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9047">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-9048">
            <div className="text-center md:text-left" data-id="element-9049">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6 shadow-2xl md:hidden" data-id="element-9050">
                <LayoutDashboardIcon className="w-8 h-8 text-white" data-id="element-9051" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-9052">
                Dashboard{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-9053">
                  Închiriere
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-9054">
                Privire de ansamblu asupra activității tale
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="flex flex-col sm:flex-row items-center gap-3" data-id="element-9055">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 flex items-center space-x-3" data-id="element-9056">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg" data-id="element-9057">
                  <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-9058" />
                </div>
                <div data-id="element-9059">
                  <p className="text-xs text-slate-300 font-medium" data-id="element-9060">
                    Contracte Semnate
                  </p>
                  <p className="text-xl font-bold text-white" data-id="element-9061">
                    {signedContracts.length}
                  </p>
                </div>
              </div>

              {urgentTasks.length > 0 && <div className="bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 rounded-2xl p-4 flex items-center space-x-3" data-id="element-9062">
                  <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg" data-id="element-9063">
                    <BellIcon className="w-6 h-6 text-white" data-id="element-9064" />
                  </div>
                  <div data-id="element-9065">
                    <p className="text-xs text-orange-100 font-medium" data-id="element-9066">
                      Task-uri urgente
                    </p>
                    <p className="text-xl font-bold text-white" data-id="element-9067">
                      {urgentTasks.filter(t => t.priority === 'high').length}
                    </p>
                  </div>
                </div>}
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 pb-16 relative z-10" data-id="element-9068">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8" data-id="element-9069">
          {dashboardStats.map((stat, index) => {
          const Icon = stat.icon;
          return <Link key={stat.label} to={stat.path} data-id="element-9070">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} whileHover={{
              y: -4,
              scale: 1.02
            }} className="bg-white rounded-2xl p-6 shadow-xl border border-slate-100 hover:shadow-2xl transition-all h-full relative overflow-hidden group" data-id="element-9071">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} data-id="element-9072" />

                  <div className="relative z-10" data-id="element-9073">
                    <div className="flex justify-between items-start mb-4" data-id="element-9074">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.iconBg} flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300`} data-id="element-9075">
                        <Icon className={`w-6 h-6 ${stat.gradient.split(' ')[1].replace('to-', 'text-')}`} data-id="element-9076" />
                      </div>
                      {stat.change && <div className="px-2 py-1 rounded-lg bg-emerald-50 text-xs font-bold text-emerald-700" data-id="element-9077">
                          {stat.change}
                        </div>}
                    </div>

                    <h3 className="text-3xl font-bold text-slate-900 mb-1 group-hover:text-[#132C53]" data-id="element-9078">
                      {stat.value}
                    </h3>
                    <p className="text-slate-500 font-medium group-hover:text-slate-700 mb-1" data-id="element-9079">
                      {stat.label}
                    </p>
                    <p className="text-xs text-slate-400 group-hover:text-slate-600" data-id="element-9080">
                      {stat.subtitle}
                    </p>
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-9081">
          {/* Recent Activity */}
          <div className="lg:col-span-2 space-y-6" data-id="element-9082">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-100" data-id="element-9083">
              <div className="flex items-center justify-between mb-6" data-id="element-9084">
                <h2 className="text-xl font-bold text-[#132C53] flex items-center" data-id="element-9085">
                  <ClockIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-9086" />
                  Activitate Recentă
                </h2>
                <Link to="/inchiriere/liste-prospectare" className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline flex items-center space-x-1" data-id="element-9087">
                  <span data-id="element-9088">Vezi tot</span>
                  <ChevronRightIcon className="w-4 h-4" data-id="element-9089" />
                </Link>
              </div>

              <div className="space-y-4" data-id="element-9090">
                {recentActivity.length > 0 ? recentActivity.map((activity, index) => {
                const Icon = activity.icon;
                return <Link key={activity.id} to={activity.path} data-id="element-9091">
                        <motion.div initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.5 + index * 0.1
                  }} className="flex items-start space-x-4 group p-4 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200 cursor-pointer" data-id="element-9092">
                          <div className={`w-10 h-10 rounded-xl ${activity.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-9093">
                            <Icon className={`w-5 h-5 ${activity.color}`} data-id="element-9094" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-9095">
                            <div className="flex justify-between items-start" data-id="element-9096">
                              <h3 className="text-sm font-bold text-slate-900" data-id="element-9097">
                                {activity.title}
                              </h3>
                              <span className="text-xs text-slate-400 font-medium whitespace-nowrap ml-2" data-id="element-9098">
                                {activity.time}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1" data-id="element-9099">
                              {activity.description}
                            </p>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-slate-300 group-hover:text-blue-600 transition-colors flex-shrink-0" data-id="element-9100" />
                        </motion.div>
                      </Link>;
              }) : <div className="text-center py-12" data-id="element-9101">
                    <ClockIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-9102" />
                    <p className="text-slate-500" data-id="element-9103">Nicio activitate recentă</p>
                  </div>}
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-gradient-to-br from-[#132C53] to-[#1e4278] rounded-3xl p-6 sm:p-8 shadow-xl text-white relative overflow-hidden" data-id="element-9104">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-9105"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20" data-id="element-9106"></div>

              <h2 className="text-xl font-bold mb-6 relative z-10 flex items-center" data-id="element-9107">
                <PlusIcon className="w-5 h-5 mr-2" data-id="element-9108" />
                Acțiuni Rapide
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10" data-id="element-9109">
                <Link to="/inchiriere/liste-prospectare" className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/20 transition-all hover:scale-[1.02]" data-id="element-9110">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg" data-id="element-9111">
                    <UploadIcon className="w-5 h-5 text-white" data-id="element-9112" />
                  </div>
                  <div className="flex-1" data-id="element-9113">
                    <span className="font-semibold block" data-id="element-9114">Importă Listă</span>
                    <span className="text-xs text-white/70" data-id="element-9115">Prospectare</span>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9116" />
                </Link>

                <Link to="/inchiriere/leaduri" className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/20 transition-all hover:scale-[1.02]" data-id="element-9117">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg" data-id="element-9118">
                    <UserPlusIcon className="w-5 h-5 text-white" data-id="element-9119" />
                  </div>
                  <div className="flex-1" data-id="element-9120">
                    <span className="font-semibold block" data-id="element-9121">Lead Chiriaș</span>
                    <span className="text-xs text-white/70" data-id="element-9122">Adaugă manual</span>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9123" />
                </Link>

                <Link to="/inchiriere/vizionari" className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/20 transition-all hover:scale-[1.02]" data-id="element-9124">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg" data-id="element-9125">
                    <CalendarPlusIcon className="w-5 h-5 text-white" data-id="element-9126" />
                  </div>
                  <div className="flex-1" data-id="element-9127">
                    <span className="font-semibold block" data-id="element-9128">Vizionare Nouă</span>
                    <span className="text-xs text-white/70" data-id="element-9129">Programează</span>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9130" />
                </Link>

                <Link to="/inchiriere/contracte/nou" className="group flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:bg-white/20 transition-all hover:scale-[1.02]" data-id="element-9131">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg" data-id="element-9132">
                    <FileSignatureIcon className="w-5 h-5 text-white" data-id="element-9133" />
                  </div>
                  <div className="flex-1" data-id="element-9134">
                    <span className="font-semibold block" data-id="element-9135">
                      Generează Contract
                    </span>
                    <span className="text-xs text-white/70" data-id="element-9136">
                      Proprietar sau Chiriaș
                    </span>
                  </div>
                  <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9137" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Tasks & Alerts */}
          <div className="space-y-6" data-id="element-9138">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="bg-white rounded-3xl p-6 shadow-xl border border-slate-100" data-id="element-9139">
              <h2 className="text-xl font-bold text-[#132C53] mb-6 flex items-center" data-id="element-9140">
                <AlertCircleIcon className="w-5 h-5 mr-2 text-orange-500" data-id="element-9141" />
                De Rezolvat Urgent
              </h2>

              <div className="space-y-3" data-id="element-9142">
                {urgentTasks.length > 0 ? urgentTasks.map((item, idx) => <div key={item.id} onClick={item.action} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200 group cursor-pointer" data-id="element-9143">
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5
                        ${item.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 group-hover:border-blue-500'}
                      `} data-id="element-9144">
                        {item.status === 'done' && <CheckCircleIcon className="w-3 h-3 text-white" data-id="element-9145" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-9146">
                        <span className={`text-sm font-semibold block ${item.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-9147">
                          {item.task}
                        </span>
                        <span className="text-xs text-slate-500 block mt-0.5" data-id="element-9148">
                          {item.description}
                        </span>
                      </div>
                      <span className={`
                        w-2 h-2 rounded-full flex-shrink-0 mt-2
                        ${item.priority === 'high' ? 'bg-red-500' : item.priority === 'medium' ? 'bg-orange-500' : 'bg-blue-500'}
                      `} data-id="element-9149" />
                    </div>) : <div className="text-center py-8" data-id="element-9150">
                    <CheckCircleIcon className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-9151" />
                    <p className="text-sm font-semibold text-slate-700" data-id="element-9152">
                      Totul la zi!
                    </p>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-9153">
                      Nu ai task-uri urgente
                    </p>
                  </div>}
              </div>
            </motion.div>

            {/* Quick Stats Summary */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-6 shadow-lg border border-slate-200" data-id="element-9154">
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-9155">
                Rezumat Rapid
              </h3>

              <div className="space-y-3" data-id="element-9156">
                <div className="flex items-center justify-between p-3 bg-white rounded-xl" data-id="element-9157">
                  <div className="flex items-center space-x-3" data-id="element-9158">
                    <PhoneIcon className="w-5 h-5 text-blue-600" data-id="element-9159" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-9160">
                      Apeluri de făcut
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-9161">
                    {pendingCalls}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-xl" data-id="element-9162">
                  <div className="flex items-center space-x-3" data-id="element-9163">
                    <HandshakeIcon className="w-5 h-5 text-emerald-600" data-id="element-9164" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-9165">
                      Colaborări
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-9166">
                    {collaborations}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-xl" data-id="element-9167">
                  <div className="flex items-center space-x-3" data-id="element-9168">
                    <CalendarIcon className="w-5 h-5 text-purple-600" data-id="element-9169" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-9170">
                      Vizionări astăzi
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-9171">
                    {upcomingViewings.length}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-xl" data-id="element-9172">
                  <div className="flex items-center space-x-3" data-id="element-9173">
                    <FileTextIcon className="w-5 h-5 text-indigo-600" data-id="element-9174" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-9175">
                      Contracte active
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-9176">
                    {activeContracts.length}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-xl" data-id="element-9177">
                  <div className="flex items-center space-x-3" data-id="element-9178">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-9179" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-9180">
                      Contracte semnate
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900" data-id="element-9181">
                    {signedContracts.length}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}